import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';

// Components
import dayjs from 'dayjs';
import { get, isEmpty, pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import ImageComponent from 'src/components/common/Image';
import { TypeContributeCompany } from 'src/features/CompanyDetail/Common/config';
import DataTable from 'src/features/CompanyDetail/Common/DataTable';
import { EmptyText, FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { LOGO_COMPANY_DEFAULT_URL } from 'src/utils/constants/imagesUrl';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { convertSnakeCaseToUpperFirst, getCompanyAgeText } from 'src/utils/helpers/stringHelpers';
import { useCompanyContribute } from 'src/zustand/useCompanyContribute';

export default function GeneralInformation({
  dataCompany,
  dataCompanyFallback,
}: {
  dataCompany: ICompanyDetail;
  dataCompanyFallback?: TradeDataCompanyDetail;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleOpenContribute } = useCompanyContribute((state) => state);
  const listWorkingHour = useMemo(() => {
    const workingHour = pick(dataCompany, [
      'monday_hours',
      'tuesday_hours',
      'wednesday_hours',
      'thursday_hours',
      'friday_hours',
      'saturday_hours',
      'sunday_hours',
    ]);
    return Object.keys(workingHour).filter((day: string) => !isEmpty(dataCompany[day as keyof ICompanyDetail]));
  }, [dataCompany]);

  const listPaymentMethod = useMemo(() => {
    const paymentMethod = pick(dataCompany, [
      'pay_check',
      'pay_credit_card',
      'pay_debit_card',
      'pay_mobile_nfc',
      'pay_snap',
    ]);
    return Object.keys(paymentMethod).filter((day: string) => dataCompany[day as keyof ICompanyDetail]);
  }, [dataCompany]);

  const listServices = useMemo(() => {
    const services = pick(dataCompany, [
      'service_from_the_business',
      'service_options',
      'service_payments',
      'service_accessibility',
      'service_highlights',
      'service_offerings',
      'service_planning',
      'service_parking',
      'service_popular_for',
      'service_dining_options',
      'service_amenities',
      'service_atmosphere',
      'service_crowd',
      'service_children',
      'service_pets',
    ]);
    const keyNotEmpty = Object.keys(services).filter(
      (day: string) => !isEmpty(dataCompany[day as keyof ICompanyDetail]),
    );
    return pick(services, keyNotEmpty);
  }, [dataCompany]);

  const listData = [
    dataCompany?.company_type
      ? {
          key: 'company_type',
          title: t('company_detail.overview.general_information.company_type'),
          value: (dataCompany?.company_type || '').toUpperCase() || EmptyText,
        }
      : null,
    dataCompany?.agent_name
      ? {
          key: 'agent_name',
          title: t('company_detail.overview.general_information.agent_name'),
          value: dataCompany?.agent_name || EmptyText,
        }
      : null,
    dataCompany?.jurisdiction || dataCompanyFallback?.jurisdiction
      ? {
          key: 'jurisdiction',
          title: t('company_detail.overview.general_information.jurisdiction'),
          value: dataCompany?.jurisdiction || dataCompanyFallback?.jurisdiction || EmptyText,
        }
      : null,
    dataCompany?.lei
      ? {
          key: 'lei',
          title: t('company_detail.overview.general_information.lei'),
          value: dataCompany?.lei || EmptyText,
        }
      : null,
    dataCompany?.date_incorporated
      ? {
          key: 'date_incorporated',
          title: t('company_detail.overview.general_information.date_incorporated'),
          value: dayjs(dataCompany.date_incorporated || '').isValid()
            ? dayjs(dataCompany.date_incorporated || '').format(FORMAT_DATE_TIME.FULL_ONLY_DATE)
            : formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, dataCompany.date_incorporated || ''),
        }
      : null,
    dataCompany?.paid_up_capital
      ? {
          key: 'paid_up_capital',
          title: t('company_detail.overview.general_information.paid_up_capital'),
          value: dataCompany?.paid_up_capital ?? EmptyText,
        }
      : null,
    dataCompany?.authorised_capital
      ? {
          key: 'authorized_capital',
          title: t('company_detail.overview.general_information.authorized_capital'),
          value: dataCompany?.authorised_capital ?? EmptyText,
        }
      : null,
    dataCompany?.date_struck_off
      ? {
          key: 'date_struck_off',
          title: t('company_detail.overview.general_information.date_struck_off'),
          value: formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, dataCompany.date_struck_off || ''),
        }
      : null,
    dataCompany?.company_age
      ? {
          key: 'company_age',
          title: t('company_detail.overview.general_information.company_age'),
          value: getCompanyAgeText(dataCompany?.company_age),
        }
      : null,
    dataCompany?.number_of_employees
      ? {
          key: 'number_employees',
          title: t('company_detail.overview.general_information.number_employees'),
          value: dataCompany?.number_of_employees || EmptyText,
        }
      : null,
    dataCompany?.registered_address
      ? {
          key: 'registered_address',
          title: t('company_detail.overview.general_information.registered_address'),
          value: (dataCompany?.registered_address || '').toUpperCase() || EmptyText,
        }
      : null,
    !isEmpty(dataCompany?.phone_numbers)
      ? {
          key: 'phone',
          title: t('company_detail.overview.general_information.phone'),
          value: !isEmpty(dataCompany?.phone_numbers)
            ? getArrayDefault(dataCompany?.phone_numbers).join(', ')
            : EmptyText,
        }
      : null,
    !isEmpty(dataCompany?.fax_numbers)
      ? {
          key: 'fax',
          title: t('company_detail.overview.general_information.fax'),
          value: !isEmpty(dataCompany?.fax_numbers) ? getArrayDefault(dataCompany?.fax_numbers).join(', ') : EmptyText,
        }
      : null,
    !isEmpty(dataCompany?.emails)
      ? {
          key: 'email',
          title: t('company_detail.overview.general_information.email'),
          value: (
            <Stack direction="row" spacing={1} alignItems="center">
              <>{!isEmpty(dataCompany?.emails) ? getArrayDefault(dataCompany?.emails).join(', ') : EmptyText}</>
              <Tooltip title="Contribute data">
                <IconButton size="small" onClick={() => handleOpenContribute(TypeContributeCompany.Email)}>
                  <AddCircleOutlineOutlined color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        }
      : null,
    !isEmpty(dataCompany?.websites)
      ? {
          key: 'website',
          title: t('company_detail.overview.general_information.website'),
          value: (
            <Stack direction="row" spacing={1} alignItems="center">
              <>{!isEmpty(dataCompany?.websites) ? getArrayDefault(dataCompany?.websites).join(', ') : EmptyText}</>
              <Tooltip title="Contribute data">
                <IconButton
                  size="small"
                  onClick={() => handleOpenContribute(TypeContributeCompany.WebsiteCompanyProfile)}
                >
                  <AddCircleOutlineOutlined color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        }
      : null,
    !isEmpty(listWorkingHour)
      ? {
          key: 'working_hour',
          title: t('company_detail.overview.general_information.working_hour'),
          value: (
            <Stack direction="column" spacing={1}>
              {listWorkingHour.map((workingHour, index) => (
                <Stack key={`${workingHour}-${index}`} direction="row" spacing={3}>
                  <Typography variant="caption2" width="40%" color="text.secondary">
                    {t(`company_detail.overview.general_information.working_hour.${workingHour}`)}
                  </Typography>
                  <Typography variant="caption2" width="60%" color="text.secondary">
                    {get(dataCompany, workingHour, '')}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ),
        }
      : null,
    !isEmpty(dataCompany?.location_image)
      ? {
          key: 'location_image',
          title: t('company_detail.overview.general_information.location_image'),
          value: (
            <ImageComponent
              imgSrc={dataCompany?.location_image || ''}
              width={64}
              height={64}
              fallbackImage={LOGO_COMPANY_DEFAULT_URL}
              style={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
              }}
            />
          ),
        }
      : null,
    !isEmpty(listPaymentMethod)
      ? {
          key: 'payment_method',
          title: t('company_detail.overview.general_information.payment_method'),
          value: (
            <Stack direction="column" spacing={1}>
              {listPaymentMethod.map((method, index) => (
                <Typography key={`${method}-${index}`} variant="body2" color="text.secondary">
                  {t(`company_detail.overview.general_information.payment_method.${method}`)}
                </Typography>
              ))}
            </Stack>
          ),
        }
      : null,
    ...Object.keys(listServices).map((serviceType) => {
      const dataService = getArrayDefault(get(listServices, serviceType, [])) as Array<string>;
      return {
        key: serviceType,
        title: t(`company_detail.overview.general_information.${serviceType}`),
        value: (
          <Stack direction="column" spacing={1}>
            {dataService.map((service, index) => (
              <Typography key={`${service}-${index}`} variant="body2" color="text.secondary">
                {convertSnakeCaseToUpperFirst(service)}
              </Typography>
            ))}
          </Stack>
        ),
      };
    }),
  ];
  return (
    <React.Fragment>
      <Typography variant="h5" align="left">
        {t('company_detail.overview.general_information.working_hour.title')}
      </Typography>
      <DataTable listData={listData} />
    </React.Fragment>
  );
}
